// Database-backed NotificationsService
// Replaces localStorage with API calls to the social backend

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { API_BASE } from '../config/api';
import AuthService from './authService';

class NotificationsServiceDB {
  constructor() {
    this.subscribers = [];
    this.isPolling = false;
    this.pollInterval = null;
    this.lastFetchTime = null;
    this.consecutiveFailures = 0;
    this.baseInterval = 60000;
    this._visibilityHandler = null;
    // SSE live stream (see _startStream). When healthy it carries updates
    // instantly and the poll backs off to a slow safety-net reconcile.
    this._streamCtrl = null;
    this._streamHealthy = false;
    this._streamWarned = false; // warn-once on stream error, reset on reconnect
    this._streamReconnectTimer = null;
    this._streamReconnectAttempts = 0;
    // Whoever shows the unread count (useUnreadNotificationCount). Every
    // response that carries the count — the bell's list and the count route
    // alike — is handed to them, so the You tab's badge reads the number the
    // bell just read (C292).
    this.countListeners = new Set();
    // Open trip planners: `trip_changed` (useTripState re-reads when someone
    // else edits the trip) and `trip_presence` (useTripPresence) on the same
    // stream. Event name -> Set of listeners.
    this.tripListeners = new Map([['trip_changed', new Set()], ['trip_presence', new Set()]]);
  }

  // Anyone the stream serves: the bell's subscribers or an open planner.
  _hasStreamListeners() {
    if (this.subscribers.length > 0) return true;
    for (const set of this.tripListeners.values()) if (set.size > 0) return true;
    return false;
  }

  _onTripEvent(event, listener) {
    this.tripListeners.get(event).add(listener);
    this._startStream(); // the planner has no bell of its own to open it
    return () => {
      this.tripListeners.get(event).delete(listener);
      if (!this._hasStreamListeners()) this._stopStream();
    };
  }

  // Hear `trip_changed` ({ trip_id, origin }); returns the unsubscribe.
  onTripChanged(listener) { return this._onTripEvent('trip_changed', listener); }

  // Hear `trip_presence` ({ trip_id, present: [userId] }); returns the unsubscribe.
  onTripPresence(listener) { return this._onTripEvent('trip_presence', listener); }

  // Hand a fresh unread count to every count listener.
  _publishUnreadCount(count) {
    if (typeof count !== 'number') return;
    this.countListeners.forEach(listener => {
      try {
        listener(count);
      } catch (error) {
        console.error('Error notifying count listener:', error);
      }
    });
  }

  // Listen for every unread count a response carries; returns the unsubscribe.
  onUnreadCount(listener) {
    this.countListeners.add(listener);
    return () => this.countListeners.delete(listener);
  }

  static API_SOCIAL = `${API_BASE}/api/social`;

  // Use centralized AuthService.handleResponse for auth error handling

  _isHidden() {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  // Start real-time polling for notifications
  startPolling(intervalMs = 60000) {
    if (this.isPolling) return;

    this.isPolling = true;
    this.baseInterval = intervalMs;

    if (typeof document !== 'undefined' && !this._visibilityHandler) {
      this._visibilityHandler = () => {
        if (!this.isPolling) return;
        if (this._isHidden()) {
          if (this.pollInterval) {
            clearTimeout(this.pollInterval);
            this.pollInterval = null;
          }
        } else {
          // Fire once on resume so the user sees fresh state, then resume the schedule
          this._pollOnce().then(() => this._schedulePoll());
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }

    this._schedulePoll();

    // Initial fetch
    this._pollOnce();
  }

  _schedulePoll() {
    if (!this.isPolling || this._isHidden()) return;
    // When the SSE stream is healthy it delivers updates instantly, so the poll
    // drops to a slow 5-min reconcile (catches anything a dropped event missed)
    // instead of hammering the backend every 60s. When the stream is down the
    // poll is the primary channel and uses the normal cadence with backoff.
    const base = this._streamHealthy ? 300000 : this.baseInterval;
    // Back off on failure: base -> 2x -> 4x -> max 5min
    const backoff = Math.min(base * Math.pow(2, this.consecutiveFailures), 300000);
    this.pollInterval = setTimeout(() => {
      this._pollOnce().then(() => this._schedulePoll());
    }, backoff);
  }

  async _pollOnce() {
    try {
      const result = await this.getNotifications(false, 50);
      this.consecutiveFailures = 0;
      this.notifySubscribers(result.notifications || []);
    } catch (error) {
      // Only log on first failure, not every retry
      if (this.consecutiveFailures === 0) {
        console.warn('Notification polling failed, will retry with backoff');
      }
      this.consecutiveFailures++;
    }
  }

  // Stop polling
  stopPolling() {
    if (this.pollInterval) {
      clearTimeout(this.pollInterval);
      this.pollInterval = null;
    }
    if (this._visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.isPolling = false;
    this.consecutiveFailures = 0;
  }

  // Open the live SSE stream. A fetch-based EventSource is used (not the native
  // one) so it can send the JWT Authorization header — same auth as every other
  // call, no token-in-URL. On any event we just re-fetch from the source of
  // truth (cheap, and avoids trusting a partial payload). The 60s poll stays up
  // as a fallback: while the stream is healthy it backs off to 5 min, and if the
  // stream drops, the poll returns to its normal cadence.
  _startStream() {
    if (this._streamCtrl) return; // already open
    if (typeof window === 'undefined' || typeof window.fetch === 'undefined') return;
    if (!AuthService.getAuthToken || !AuthService.getAuthToken()) return; // not authed → poll only

    const ctrl = new AbortController();
    this._streamCtrl = ctrl;

    fetchEventSource(`${NotificationsServiceDB.API_SOCIAL}/notifications/stream`, {
      signal: ctrl.signal,
      headers: AuthService.getJsonHeaders(),
      // Close on hidden tabs and reopen on focus — mirrors the poll's
      // visibility gating so a backgrounded tab holds no open connection.
      openWhenHidden: false,
      onopen: async (res) => {
        const ct = res.headers.get('content-type') || '';
        if (res.ok && ct.includes('text/event-stream')) {
          this._streamHealthy = true;
          this._streamWarned = false; // healthy again — re-arm the warn-once
          this._streamReconnectAttempts = 0; // connected — reset the backoff
          // A reopen (after a hidden tab, a deploy, a blip): whatever was
          // published meanwhile was missed, so open planners re-read.
          if (this._streamOpenedBefore) this._publishTripEvent('trip_changed', { trip_id: null, reconnected: true });
          this._streamOpenedBefore = true;
          return;
        }
        // Auth failure / wrong response. fetchEventSource sends a throw here
        // to onerror, which rethrows it (below) to end the library's loop —
        // left to the library it retried every second with the headers it
        // copied at the start, so an expired token 401'd forever. The catch
        // reopens through _startStream, with fresh headers, on our backoff.
        this._streamHealthy = false;
        const error = new Error(`SSE open failed: ${res.status}`);
        error.streamFatal = true;
        throw error;
      },
      onmessage: (msg) => {
        // 'notification' (new row) or 'count' (initial sync) → refresh.
        if (msg.event === 'notification' || msg.event === 'count') {
          this.notifySubscribers();
        } else if (this.tripListeners.has(msg.event)) {
          let data = null;
          try { data = JSON.parse(msg.data); } catch (_) { return; }
          this._publishTripEvent(msg.event, data);
        }
      },
      onerror: (err) => {
        if (err && err.streamFatal) throw err; // see onopen
        // Transient: record unhealthy and let fetchEventSource retry with
        // backoff (returning, not throwing). Poll covers the gap meanwhile.
        this._streamHealthy = false;
        if (!this._streamWarned) {
          this._streamWarned = true;
          console.warn('Notification stream error, falling back to polling');
        }
      },
      onclose: () => {
        // A CLEAN end — the server shutting down for a deploy, or a proxy
        // idling us out. fetchEventSource does NOT retry here: its retry timer
        // is armed only in its catch branch, so a clean end resolves the
        // promise and disposes the stream. Without re-arming, this tab would
        // sit on the 60s poll with no live badge until a full page reload.
        this._streamHealthy = false;
        this._streamCtrl = null; // release the _startStream latch
        this._scheduleStreamReconnect();
      }
    }).catch((err) => {
      // Aborted (stop) or fatal (auth) — poll remains the fallback. A fatal
      // open reopens on the backoff with fresh headers (the access token is
      // refreshed in the background), unless the stream was stopped.
      this._streamHealthy = false;
      if (err && err.streamFatal && this._streamCtrl === ctrl) {
        this._streamCtrl = null;
        this._scheduleStreamReconnect();
      }
    });
  }

  _publishTripEvent(event, data) {
    this.tripListeners.get(event).forEach(listener => {
      try { listener(data); } catch (error) { console.error('Error notifying trip listener:', error); }
    });
  }

  // Reopen after a clean close, with backoff. During a deploy the new instance
  // is not listening yet; that attempt fails at the network level, which IS
  // fetchEventSource's retry path, so it takes over from there. The backoff
  // only bounds this handler, so a server that closes cleanly on every connect
  // degrades to slow retries instead of a hot loop.
  _scheduleStreamReconnect() {
    if (this._streamReconnectTimer) return;
    if (!this._hasStreamListeners()) return; // nobody listening
    const delay = Math.min(30000, 1000 * Math.pow(2, this._streamReconnectAttempts));
    this._streamReconnectAttempts++;
    this._streamReconnectTimer = setTimeout(() => {
      this._streamReconnectTimer = null;
      if (this._hasStreamListeners()) this._startStream();
    }, delay);
  }

  _stopStream() {
    if (this._streamReconnectTimer) {
      clearTimeout(this._streamReconnectTimer); // don't reopen after unsubscribe
      this._streamReconnectTimer = null;
    }
    this._streamReconnectAttempts = 0;
    if (this._streamCtrl) {
      this._streamCtrl.abort();
      this._streamCtrl = null;
    }
    this._streamHealthy = false;
    this._streamOpenedBefore = false; // the next open is a fresh start, not a reopen
  }

  // Get notifications from database API
  // `types` (optional array) asks the server for only those notification
  // types, filtered before its limit applies.
  async getNotifications(unreadOnly = false, limit = 50, types = null) {
    try {
      const typeParam = Array.isArray(types) && types.length > 0
        ? `&types=${encodeURIComponent(types.join(','))}`
        : '';
      const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications?unreadOnly=${unreadOnly}&limit=${limit}${typeParam}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      const unreadCount = data.unreadCount || 0;
      this._publishUnreadCount(unreadCount);
      return {
        notifications: data.notifications || [],
        unreadCount
      };
    } catch (error) {
      // Network errors (server down) are expected during development — don't spam console
      if (error.name !== 'TypeError') {
        console.error('Error getting notifications:', error);
      }
      return { notifications: [], unreadCount: 0 };
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);
      this.notifySubscribers(); // Refresh for subscribers
      return result;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    try {
      const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications/read-all`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);
      this.notifySubscribers(); // Refresh for subscribers
      return result;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Archive one notification (the per-row X), no refresh.
  async _archive(notificationId) {
    const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: AuthService.getJsonHeaders()
    });
    return AuthService.handleResponse(response);
  }

  // Remove notification
  async removeNotification(notificationId) {
    try {
      const result = await this._archive(notificationId);
      this.notifySubscribers(); // Refresh for subscribers
      return result;
    } catch (error) {
      console.error('Error removing notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Remove several notifications: one request each, then ONE refresh for
  // them all (C290 — removeNotification per row refreshed every subscriber
  // per row, about five requests each). No bulk route takes a list of ids;
  // Clear all's own route archives by type, which these rows are not.
  // Returns one result per id, a failed one as { success: false, error }.
  async removeNotifications(notificationIds) {
    const ids = Array.isArray(notificationIds) ? notificationIds : [];
    if (ids.length === 0) return [];
    const results = await Promise.all(ids.map(id => this._archive(id).catch(error => {
      console.error('Error removing notification:', error);
      return { success: false, error: error.message };
    })));
    this.notifySubscribers();
    return results;
  }

  // Clear all notifications — archives every non-actionable notification.
  // Actionable ones (friend requests, trip invites, …) are intentionally kept
  // by the backend so the user can still respond to them.
  // `{ notify: false }` leaves the refresh to the caller: the bell's Clear all
  // archives a few more rows after this and refreshes once for both (C308).
  async clearAllNotifications({ notify = true } = {}) {
    try {
      const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);
      if (notify) this.notifySubscribers(); // Refresh for subscribers
      return result;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      return { success: false, error: error.message };
    }
  }

  // Get unread count — hits the dedicated count endpoint (pure COUNT, no row
  // payload) rather than fetching a notification list just to read the number.
  async getUnreadCount() {
    try {
      const response = await fetch(`${NotificationsServiceDB.API_SOCIAL}/notifications/count`, {
        headers: AuthService.getJsonHeaders()
      });
      const result = await AuthService.handleResponse(response);
      const count = result.count || 0;
      this._publishUnreadCount(count);
      return count;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Get urgent count (approximate - filter client-side)
  async getUrgentCount() {
    try {
      const result = await this.getNotifications(true, 50);
      const urgentNotifications = (result.notifications || []).filter(n => n.urgent);
      return urgentNotifications.length;
    } catch (error) {
      console.error('Error getting urgent count:', error);
      return 0;
    }
  }

  // === VISIT COMPANION TAG METHODS ===

  async confirmVisitTag(activityId) {
    try {
      const response = await fetch(`${API_BASE}/api/user/visit-companion/confirm`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ activity_id: activityId })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error confirming visit tag:', error);
      return { success: false, error: error.message };
    }
  }

  async skipVisitTag(activityId) {
    try {
      const response = await fetch(`${API_BASE}/api/user/visit-companion/skip`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ activity_id: activityId })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error skipping visit tag:', error);
      return { success: false, error: error.message };
    }
  }

  async denyVisitTag(activityId, notificationId) {
    try {
      const response = await fetch(`${API_BASE}/api/user/visit-companion/deny`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ activity_id: activityId, notification_id: notificationId })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error denying visit tag:', error);
      return { success: false, error: error.message };
    }
  }

  // === SUBSCRIPTION METHODS ===

  subscribe(callback) {
    this.subscribers.push(callback);

    // Start polling + live stream when first subscriber joins
    if (this.subscribers.length === 1) {
      this.startPolling();
      this._startStream();
    }

    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);

      // Stop polling when the last subscriber leaves; the stream too, unless
      // an open planner still listens on it.
      if (this.subscribers.length === 0) {
        this.stopPolling();
        if (!this._hasStreamListeners()) this._stopStream();
      }
    };
  }

  async notifySubscribers(notifications = null) {
    if (!notifications) {
      try {
        const result = await this.getNotifications(false, 50);
        notifications = result.notifications || [];
      } catch (error) {
        console.error('Error fetching notifications for subscribers:', error);
        return;
      }
    }

    this.subscribers.forEach(callback => {
      try {
        callback(notifications);
      } catch (error) {
        console.error('Error notifying subscriber:', error);
      }
    });
  }

  // === UTILITY METHODS (keep existing) ===

  formatTimeUntil(milliseconds) {
    const minutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `in ${days} day${days !== 1 ? 's' : ''}`;
    if (hours > 0) return `in ${hours} hour${hours !== 1 ? 's' : ''}`;
    if (minutes > 0) return `in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    return 'starting soon';
  }

  // === COMPATIBILITY METHODS (for existing UI) ===

  // Filter notifications client-side to match old API
  filterNotifications(notifications, filter = {}) {
    let filtered = [...notifications];

    if (filter.unreadOnly) {
      filtered = filtered.filter(n => !n.is_read);
    }

    if (filter.category) {
      filtered = filtered.filter(n => n.type === filter.category);
    }

    if (filter.type) {
      filtered = filtered.filter(n => n.type === filter.type);
    }

    if (filter.urgent) {
      filtered = filtered.filter(n => n.urgent);
    }

    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  // Adapter methods for existing NotificationCenter compatibility
  async getNotificationsWithFilter(filter = {}) {
    const result = await this.getNotifications(false, 100);
    return this.filterNotifications(result.notifications, filter);
  }

  // === DEMO METHODS (for testing) ===

  async addDemoNotifications() {
    console.log('Demo notifications would be created via backend API');
    // In a real implementation, these would be API calls to create notifications
    // For now, just log what would be created
  }
}

// Create singleton instance
const notificationsServiceDB = new NotificationsServiceDB();

export default notificationsServiceDB;